// @refresh reload
import './app.css';
import { ErrorHandler, Navigator, useUseCase } from '@magicdoor/solid-use-case';
import * as Sentry from '@sentry/solid';
import { MetaProvider } from '@solidjs/meta';
import { Router, useNavigate } from '@solidjs/router';
import { clientOnly } from '@solidjs/start';
import { FileRoutes } from '@solidjs/start/router';
import { onMount, Suspense } from 'solid-js';
import { CustomMeta } from '~/components/common/Meta';
import { ImagePreview } from './components/common/ImagePreview';
import { Toaster } from './components/common/toaster/Toaster';
import { NoRentalHistoryError } from './errors/validationError';
import { CompanyStyling } from './global-contexts/CompanyStyling';
import { TranslationKeys, useLocalization } from './hooks/useLocalization';
import { InitializeAppUseCase } from './use-cases/initializeUseCase';
import { getCurrentHostName } from './utils/host';

const HomeLayout = clientOnly(() => import('./components/layouts/HomeLayout'));

export default function Root() {
  const { execute: initializeApp } = useUseCase(InitializeAppUseCase);

  const { t } = useLocalization();

  onMount(() => {
    initializeApp(getCurrentHostName());
  });

  const NavigationHandler = () => {
    const navigate = useNavigate();
    return (
      <Navigator
        onNavigate={(url) => {
          if (url.startsWith('http://') || url.startsWith('https://')) {
            window.location.href = url;
          } else {
            navigate(url);
          }
        }}
      />
    );
  };

  const getMessageForError = (error?: Error): string => {
    if (error instanceof NoRentalHistoryError) {
      return t(TranslationKeys.NO_RENTAL_HISTORY_ERROR);
    }
    return error?.message || t(TranslationKeys.SOMETHING_WENT_WRONG);
  };

  return (
    <MetaProvider>
      <Router
        root={(props) => (
          <Suspense>
            <CompanyStyling>
              <ErrorHandler
                onWillReportError={(error: Error) => {
                  Sentry.captureException(error);
                  return true;
                }}
                renderErrorDialog={({ error, onClose }) => {
                  return (
                    <div class="fixed inset-0 z-[9999] flex items-center justify-center bg-black/10 p-4">
                      <div class="w-[500px] max-w-full overflow-hidden rounded-lg bg-white shadow-lg">
                        <div class="border-b border-gray-200 px-6 py-4">
                          <h2 class="text-primary text-center text-xl font-semibold">{t(TranslationKeys.OOPS)}</h2>
                        </div>
                        <div class="p-6 py-4">
                          <p class="text-center text-gray-700">{getMessageForError(error)}</p>
                        </div>
                        <div class="py-4 text-center">
                          <button
                            class="rounded-full bg-primary-color px-6 py-2 font-bold text-white hover:bg-hover-color"
                            onClick={onClose}>
                            {t(TranslationKeys.CLOSE)}
                          </button>
                        </div>
                      </div>
                    </div>
                  );
                }}>
                {' '}
                <NavigationHandler />
                <CustomMeta />
                <HomeLayout>{props.children}</HomeLayout>
                <ImagePreview />
                <Toaster />
              </ErrorHandler>
            </CompanyStyling>
          </Suspense>
        )}>
        <FileRoutes />
      </Router>
    </MetaProvider>
  );
}
